// ── Centralized appointment-type list for NETWORK scheduling flows ──────────
//
// One source of truth for the three network-scheduling entry points:
//   • CRM Queue → Schedule modal (network branch)
//   • Network Scheduling → Post Availability modal
//   • Network Scheduling → Reschedule modal
//
// Each entry: { code, label, category }. `code` is the exact 5-letter
// Raintree appointment-type code we send in the
// /webhook/raintree-network-book payload. Discipline is auto-derived
// server-side (services/raintree_client.py::appt_type_to_network_discipline)
// — DO NOT add a discipline field here.
//
// Excluded on purpose (DO NOT add):
//   • Telehealth: THEVA, THRA, THRE, THTX
//       — n8n spec flags the TH discipline code as provisional; booking
//         these would likely fail at Raintree until that's confirmed.
//   • Reserve: RVNP, RVRA
//       — the n8n workflow auto-remaps these to PTEVA/PTRA internally,
//         so there's no reason to surface them as separate user choices.
//
// When the TH discipline code is confirmed by Raintree, append the four
// telehealth codes here and the dropdowns pick them up automatically
// across all three screens.

export const NETWORK_APPOINTMENT_TYPES = [
  // Physical Therapy — discipline = PT
  { code: "PTEVA", label: "PT Evaluation",        category: "Physical Therapy" },
  // NPEVA / NPRA — network-only appointment types added per Nancy at
  // WestStar (clinical stakeholder). Used for network billing alongside
  // the regular PT codes. Discipline = PT (see raintree_client.py).
  { code: "NPEVA", label: "Network Patient Evaluation", category: "Physical Therapy" },
  { code: "PTRA",  label: "PT Re-Admit",          category: "Physical Therapy" },
  { code: "NPRA",  label: "Network Patient Re-Admit",   category: "Physical Therapy" },
  { code: "PTTX",  label: "PT Daily Treatment",   category: "Physical Therapy" },
  { code: "PTRE",  label: "PT Re-Eval",           category: "Physical Therapy" },
  { code: "PTPN",  label: "PT Progress Visit",    category: "Physical Therapy" },
  { code: "PTO",   label: "PTO",                  category: "Physical Therapy" },

  // Acupuncture — case discipline = AP
  // (verified against real Raintree case records: ACEVA → caseId AP001)
  { code: "ACEVA", label: "Acupuncture New Patient", category: "Acupuncture" },
  { code: "ACREV", label: "Acupuncture Re-Admit",    category: "Acupuncture" },

  // Aquatic — case discipline = AT
  // (verified against real Raintree case records: AQEVA → caseId AT001; NOT AC, NOT AQ)
  { code: "AQEVA", label: "Aquatic New Patient", category: "Aquatic" },
  { code: "AQREV", label: "Aquatic Re-Admit",    category: "Aquatic" },
];

// Default for any modal where no existing code is being preserved.
export const NETWORK_APPOINTMENT_TYPE_DEFAULT = "PTEVA";

// Ordered category list — keeps optgroup order stable across renders
// regardless of which type was selected first.
export const NETWORK_APPOINTMENT_TYPE_CATEGORIES = [
  "Physical Therapy",
  "Acupuncture",
  "Aquatic",
];

// Group helper consumed by the dropdowns. Avoids re-running the groupBy
// on every render — the list is static.
export const NETWORK_APPOINTMENT_TYPES_BY_CATEGORY = NETWORK_APPOINTMENT_TYPE_CATEGORIES.map((category) => ({
  category,
  types: NETWORK_APPOINTMENT_TYPES.filter((t) => t.category === category),
}));

// Returns true if the given code is one we surface in the dropdown.
// Used by reschedule to decide whether to preserve the original code
// or fall back to PTEVA (e.g., if a legacy request was posted with
// a now-excluded TH code).
export function isSupportedNetworkApptType(code) {
  return NETWORK_APPOINTMENT_TYPES.some((t) => t.code === code);
}
